import { getAccessProfileQueryKey } from "@packages/api-client/hooks/authorizationController";
import {
	getMyOrganizationsQueryKey,
	useCreateOrganization as useCreateOrganizationBase,
	useDeleteOrganization as useDeleteOrganizationBase,
	useLeaveOrganization as useLeaveOrganizationBase,
	useUpdateOrganization as useUpdateOrganizationBase,
} from "@packages/api-client/hooks/organizationController";
import type { GetAccessProfileQueryResponse } from "@packages/api-client/types/authorizationController";
import type { GetMyOrganizationsQueryResponse } from "@packages/api-client/types/organizationController";
import { COUNTRY_CODES, type CountryCode } from "@packages/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// Re-export Kubb types for consumers

// Re-export Kubb query keys for cache compatibility

// ─── Country ──────────────────────────────────────────────────────────────

/**
 * The organization country field, shared by create and settings forms.
 *
 * `POST /organizations` requires `country`, and the value decides which sanitary
 * calendar the new organization is seeded with. `PUT /organizations/:id` accepts
 * it as an optional update and does not re-seed existing vaccination rules.
 * Forms bind this schema so a missing or unknown code is rejected before a
 * request is ever made — the option set comes from `COUNTRY_CODES` so the form,
 * the API enum and the generated client cannot drift apart.
 */
export const countryFieldSchema = z.enum(COUNTRY_CODES);

/** Preselected so the field is never submittable empty. */
export const DEFAULT_COUNTRY_CODE: CountryCode = "BR";

export { COUNTRY_CODES, type CountryCode };

/**
 * Localized country name, falling back to the raw code so an unmapped country
 * renders as `PY` rather than as a blank option.
 */
export const countryLabel = (
	t: (key: string, options?: { defaultValue: string }) => string,
	code: string,
): string => t(`country.${code}`, { defaultValue: code });

// ─── Mutation Hooks ──────────────────────────────────────────────────────────

export const useCreateOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useCreateOrganizationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.creating"),
					success: t("common.createSuccess"),
				},
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: getMyOrganizationsQueryKey(),
				});
			},
		},
	});
};

export const useUpdateOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useUpdateOrganizationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.updating"),
					success: t("common.updateSuccess"),
				},
			},
			// The organization name is cached twice: in the org list and as
			// `orgName` on the access profile that feeds the org switcher and the
			// mobile header. Both are patched here — synchronously, because
			// TanStack awaits `onSuccess` but drops the `invalidateQueries`
			// promise, so callers can only rely on what `setQueryData` wrote.
			onSuccess: (updated, variables) => {
				// Merge, don't replace: list items carry `role`, the response does not.
				queryClient.setQueryData<GetMyOrganizationsQueryResponse>(
					getMyOrganizationsQueryKey(),
					(old) =>
						old?.map((org) =>
							org.id === updated.id ? { ...org, ...updated } : org,
						),
				);

				// Patched, never invalidated: the write response is authoritative for
				// the only access-profile field derived from the organization, so a
				// refetch would reconcile nothing and cost a request per rename.
				queryClient.setQueryData<GetAccessProfileQueryResponse>(
					getAccessProfileQueryKey({
						organizationId: variables.organizationId,
					}),
					(old) => (old ? { ...old, orgName: updated.name } : old),
				);

				// The list is ordered by name server-side; a local patch cannot reorder it.
				queryClient.invalidateQueries({
					queryKey: getMyOrganizationsQueryKey(),
				});
			},
		},
	});
};

export const useLeaveOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useLeaveOrganizationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.leaving"),
					success: t("common.leaveSuccess"),
				},
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: getMyOrganizationsQueryKey(),
				});
			},
		},
	});
};

export const useDeleteOrganization = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useDeleteOrganizationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("organization.toasts.deleting"),
					success: t("organization.toasts.deleteSuccess"),
				},
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: getMyOrganizationsQueryKey(),
				});
			},
		},
	});
};
